import { useState } from 'react';
import { Button, Chip, InlineAlert } from '@/contasis';
import {
  MAXIMO_COMENTARIO_BREVE_RETROALIMENTACION,
  PUNTUACION_NPS_MAXIMA,
  PUNTUACION_NPS_MINIMA,
} from '../constantes';
import { useToast } from '@/contexts/ContextoToast';
import { CampoComentarioBreve } from './CampoComentarioBreve';
import { useEstadoEnvioLocal } from '../hooks/useEstadoEnvioLocal';
import { useRetroalimentacion } from '../hooks/useRetroalimentacion';
import { normalizarTextoBreve } from '../reglasFrecuencia';

export function EncuestaNps() {
  const { enviarRespuestaNps, cerrarPanel } = useRetroalimentacion();
  const { showError, showWarning } = useToast();
  const [puntuacion, setPuntuacion] = useState<number | null>(null);
  const [comentario, setComentario] = useState('');
  const { enviado, enviando, ejecutarEnvioLocal, reiniciarEstadoEnvio } = useEstadoEnvioLocal();

  const comentarioNormalizado = normalizarTextoBreve(comentario);
  const puedeEnviar = puntuacion !== null && !enviando;

  const seleccionarPuntuacion = (valor: number) => {
    setPuntuacion(valor);

    if (enviado) {
      reiniciarEstadoEnvio();
    }
  };

  const actualizarComentario = (valor: string) => {
    setComentario(valor);

    if (enviado) {
      reiniciarEstadoEnvio();
    }
  };

  const enviar = async () => {
    if (puntuacion === null) {
      showWarning('Selecciona una calificación antes de continuar.');
      return;
    }

    const ok = await ejecutarEnvioLocal(() => enviarRespuestaNps({
      puntuacion,
      comentario: comentarioNormalizado || undefined,
    }));

    if (ok === 'omitido') {
      return;
    }

    if (ok === 'fallido') {
      showError('No pudimos guardar tu calificación. Inténtalo de nuevo.');
      return;
    }

    setPuntuacion(null);
    setComentario('');
  };

  if (enviado) {
    return (
      <div className="space-y-5">
        <InlineAlert variant="success" title="Gracias por tu calificación" />

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={reiniciarEstadoEnvio}>
            Calificar de nuevo
          </Button>
          <Button variant="primary" onClick={cerrarPanel}>
            Cerrar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h3 className="text-ui-base font-semibold text-primary">Tu calificación</h3>
        <p className="text-ui-sm text-tertiary">
          ¿Qué tan satisfecho estás con SenciYo?
        </p>
      </div>

      <div className="flex items-center justify-between">
        {Array.from({ length: PUNTUACION_NPS_MAXIMA }, (_, index) => index + PUNTUACION_NPS_MINIMA).map((valor) => (
          <Chip
            key={valor}
            size="sm"
            className="h-9 w-9 shrink-0 px-0"
            selected={puntuacion === valor}
            onClick={() => seleccionarPuntuacion(valor)}
            disabled={enviando}
            aria-pressed={puntuacion === valor}
          >
            {valor}
          </Chip>
        ))}
      </div>

      <div className="flex items-center justify-between text-caption text-tertiary">
        <span>Muy baja</span>
        <span>Muy alta</span>
      </div>

      {puntuacion !== null && (
        <CampoComentarioBreve
          etiqueta="Comentario opcional"
          valor={comentario}
          onChange={actualizarComentario}
          placeholder="Si quieres, agrega un contexto breve para tu puntuación."
          maximoCaracteres={MAXIMO_COMENTARIO_BREVE_RETROALIMENTACION}
          disabled={enviando}
        />
      )}

      <div className="flex justify-end">
        <Button variant="primary" onClick={enviar} disabled={!puedeEnviar} loading={enviando} loadingText="Enviando...">
          Enviar calificación
        </Button>
      </div>
    </div>
  );
}
